<template>
  <teleport v-if="pipContainer" :to="pipContainer">
    <twilio-pip-dialer @keypad:toggle="resizePipWindow" />
  </teleport>

  <!-- Call notes modal for incoming calls accepted via PiP -->
  <teleport v-if="callDialog && showCallDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeCallDialog"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  provide,
  ref,
  shallowRef,
  toRaw,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { Call, Device } from "@twilio/voice-sdk";
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  CallSessionStatus,
  CommunicationType,
} from "@/enums/communicationLogs";
import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioCallEvents,
} from "@/enums/communicationProviders";
import {
  TWILIO_PIP_ACCEPT_API_CALL,
  TWILIO_PIP_OPEN,
} from "@/helpers/constants/events";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { CALL_CHANNEL } from "@/helpers/websockets/common";
import { useEmitter } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import { useDialerApiStore } from "@/services/dialer-api";
import type { CallStateStatus } from "@/services/dialer-api/types";
import { useAnalyticsStore } from "@/stores/analytics";
import { CALL_REQUEST_TYPES, useCallsStore } from "@/stores/calls";

import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import TwilioPipDialer from "@/components/twilio/TwilioPiPDialer.vue";

import type {
  CallData,
  CallState,
  TerminalScreenConfig,
} from "@/services/dialer-api";
import type { ActiveCallSession } from "@/stores/calls";

// ============================================
// Constants
// ============================================

const twilioDebugEnabled =
  new URLSearchParams(window.location.search).has("twilio_debug") ||
  localStorage.getItem("twilio_debug") === "1";

const deviceOptions = {
  logLevel: twilioDebugEnabled ? 1 : undefined,
  closeProtection: true,
  codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  tokenRefreshMs: 30_000,
  edge: [
    "roaming",
    "ashburn",
    "umatilla",
    "sao-paulo",
    "singapore",
    "tokyo",
    "dublin",
    "frankfurt",
    "sydney",
  ],
  maxCallSignalingTimeoutMs: 30_000,
} satisfies Device.Options;

const deviceSize = {
  width: 340,
  height: 450,
  keypadWidth: 180,
} as const;

const TERMINAL_SCREEN_MAP: Record<string, TerminalScreenConfig> = {
  busy: {
    statusText: "DIALER.LINE_BUSY",
    tone: "/sounds/busy-tone.mp3",
    autoDismissMs: 4000,
    showDismissButton: true,
  },
  no_answer: {
    statusText: "DIALER.NO_ANSWER",
    tone: null,
    autoDismissMs: 2000,
    showDismissButton: false,
  },
  failed: {
    statusText: "DIALER.CALL_FAILED",
    tone: null,
    autoDismissMs: 3000,
    showDismissButton: false,
  },
  canceled: {
    statusText: "DIALER.CALL_CANCELED",
    tone: null,
    autoDismissMs: 1500,
    showDismissButton: false,
  },
  completed: {
    statusText: "DIALER.ENDING",
    tone: null,
    autoDismissMs: 800,
    showDismissButton: false,
  },
  caller_left: {
    statusText: "DIALER.CALLER_LEFT",
    tone: null,
    autoDismissMs: 2000,
    showDismissButton: false,
  },
};

const DEFAULT_TERMINAL_CONFIG: TerminalScreenConfig = {
  statusText: "DIALER.ENDING",
  tone: null,
  autoDismissMs: 800,
  showDismissButton: false,
};

// The ending window bridges the gap between SDK disconnect and the WS
// terminal event. Shows "Ending..." briefly. If a terminal status arrives
// from the API, it replaces this with the specific terminal screen.
const CALL_ENDING_MS = 800;

// ============================================
// Composables & Store Initialization
// ============================================

const emitter = useEmitter();
const { t } = useI18n();
const callStore = useCallsStore();
const dialerApi = useDialerApiStore();
const {
  activeCallSession,
  callDialog,
  callRequestData,
  isDialing,
  callDirection,
  isMicrophoneMuted,
  isRingtoneSilenced,
  device,
  showCallTimer,
} = storeToRefs(callStore);
const { showMessage } = useNotification();
const { trackEvent } = useAnalyticsStore();

const { twilioAccessToken } = storeToRefs(dialerApi);

// ============================================
// Refs
// ============================================

const pendingAutoAccept = ref(false);
const autoAcceptTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const terminalToneAudio = shallowRef<HTMLAudioElement | null>(null);
const terminalDismissTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const endingTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const terminalState = ref<{
  status: string;
  config: TerminalScreenConfig;
  callData: CallData;
} | null>(null);
const isEnding = ref(false);
const pipContainer = shallowRef<HTMLElement | null>(null);
const pipWindow = shallowRef<Window | null>(null);
const showCallDialog = ref(false);
const subscribedCallId = ref<string | null>(null);
const isAcceptingCall = ref(false);

// ============================================
// Computeds
// ============================================

const isSupported = computed(() => "documentPictureInPicture" in window);

// ============================================
// Methods
// ============================================

const setPendingAutoAccept = (pending: boolean) => {
  pendingAutoAccept.value = pending;

  if (autoAcceptTimeoutId.value) {
    clearTimeout(autoAcceptTimeoutId.value);
    autoAcceptTimeoutId.value = null;
  }

  if (pending) {
    autoAcceptTimeoutId.value = setTimeout(() => {
      if (pendingAutoAccept.value) {
        pendingAutoAccept.value = false;
      }
      autoAcceptTimeoutId.value = null;
    }, 10000);
  }
};

const cleanupDevice = () => {
  // Note: Do NOT clear pipWindow/pipContainer here - they should only be cleared
  // when PiP is actually closed (via pagehide event), not when a call ends.
  // Clearing them while PiP is still open causes white screen issues.
  const currentDevice = toRaw(device.value);
  if (!currentDevice) {
    return;
  }
  currentDevice.unregister();
  currentDevice.removeAllListeners();
  currentDevice.destroy();
  callStore.setDevice(null);
};

const cancelCallEnding = () => {
  if (endingTimer.value) {
    clearTimeout(endingTimer.value);
    endingTimer.value = null;
  }
};

const startCallEnding = () => {
  cleanupDevice();
  isEnding.value = true;

  if (endingTimer.value) {
    clearTimeout(endingTimer.value);
  }
  endingTimer.value = setTimeout(() => {
    endingTimer.value = null;
    isEnding.value = false;
    if (terminalState.value) {
      return;
    }
    callStore.clearCallState();
    dialerApi.clearCallState();
  }, CALL_ENDING_MS);
};

const dismissTerminalState = () => {
  callStore.isTerminalScreenActive = false;
  cancelCallEnding();
  terminalState.value = null;
  isEnding.value = false;

  if (terminalToneAudio.value) {
    terminalToneAudio.value.pause();
    terminalToneAudio.value.currentTime = 0;
    terminalToneAudio.value = null;
  }
  if (terminalDismissTimer.value) {
    clearTimeout(terminalDismissTimer.value);
    terminalDismissTimer.value = null;
  }

  callStore.clearCallState();
  cleanupDevice();
};

const handleTerminalState = (callState: CallState) => {
  callStore.isTerminalScreenActive = true;
  cancelCallEnding();

  const config =
    TERMINAL_SCREEN_MAP[callState.status] ?? DEFAULT_TERMINAL_CONFIG;

  terminalState.value = {
    status: callState.status,
    config,
    callData: callState.call_data,
  };
  isEnding.value = false;

  callDirection.value =
    callState.call_data.direction === "inbound"
      ? TwilioCallDirection.Incoming
      : TwilioCallDirection.Outgoing;
  activeCallSession.value = {
    to: callState.call_data.to || "",
    data: {
      from: callState.call_data.from,
      callee: {
        name: callState.call_data.caller_name || "",
      },
    },
  } as typeof activeCallSession.value;

  if (config.tone) {
    terminalToneAudio.value = new Audio(config.tone);
    terminalToneAudio.value.volume = 1;
    terminalToneAudio.value.play().catch((err) => {
      Sentry.captureException(err);
    });
  }

  terminalDismissTimer.value = setTimeout(() => {
    dismissTerminalState();
  }, config.autoDismissMs);
};

const fetchAccessToken = async (): Promise<string | null> => {
  try {
    const response = await dialerApi.getAccessToken();
    return response.token;
  } catch (error) {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: "fetch_access_token_failed",
      explanation: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

const copyStyles = (pip: Window) => {
  document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    pip.document.head.appendChild(node.cloneNode(true));
  });
};

const openPiP = async (options?: {
  width?: number;
  height?: number;
}): Promise<Window | null> => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return null;
  }
  if (pipWindow.value && !pipWindow.value.closed) {
    pipWindow.value.close();
  }
  try {
    // @ts-expect-error - documentPictureInPicture is not in TypeScript types yet
    const pip = await window.documentPictureInPicture.requestWindow({
      width: options?.width ?? deviceSize.width,
      height: options?.height ?? deviceSize.height,
    });
    pipWindow.value = pip;
    copyStyles(pip);
    return pip;
  } catch {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.PiPError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_OPEN_PIP"),
    });
    showMessage(t("ERRORS.TWILIO.FAILED_TO_OPEN_PIP"), "error");
    return null;
  }
};

const resizePipWindow = (isKeypadVisible: boolean) => {
  if (!pipWindow.value || pipWindow.value.closed) {
    return;
  }

  const targetInnerWidth = isKeypadVisible
    ? pipWindow.value.outerWidth + deviceSize.keypadWidth
    : pipWindow.value.outerWidth - deviceSize.keypadWidth;

  const targetOuterWidth = Math.round(targetInnerWidth);
  const targetOuterHeight = Math.round(pipWindow.value.outerHeight);

  pipWindow.value.resizeTo(targetOuterWidth, targetOuterHeight);
};

const closeCallDialog = () => {
  showCallDialog.value = false;
  callDialog.value = null;
};

const handlePiPClose = () => {
  callStore.togglePipState(false);
  pipWindow.value = null;
  pipContainer.value = null;
  trackEvent(AnalyticsEvent.TwilioPipClosed);
};

const handleCallEnded = () => {
  dialerApi.stopHeartbeat();
  trackEvent(AnalyticsEvent.TwilioCallEnded, {
    direction: callDirection.value,
  });
  // Unsubscribe from call channel when call ends
  if (subscribedCallId.value) {
    leaveChannel(CALL_CHANNEL, subscribedCallId.value);
    subscribedCallId.value = null;
  }
  if (terminalState.value) {
    return;
  }
  startCallEnding();
};

/**
 * Handle incoming call from Twilio Device.
 * If pendingAutoAccept is true (user clicked Accept, API-initiated flow),
 * automatically accept the call.
 */
const handleIncomingCall = (call: Call) => {
  // If we're expecting this call (user clicked Accept), auto-accept it
  if (pendingAutoAccept.value) {
    setPendingAutoAccept(false);

    call.accept();
    callStore.setActiveCall(call);
    showCallTimer.value = true;
    trackEvent(AnalyticsEvent.TwilioIncomingCall);

    // Start heartbeat for long calls
    dialerApi.startHeartbeat();

    // Subscribe to call channel for updates
    const apiCallData = callStore.apiIncomingCallData;
    if (apiCallData?.sessionId) {
      subscribedCallId.value = apiCallData.sessionId;
      subscribeToChannelEvents(CALL_CHANNEL, subscribedCallId.value);
    }

    // Set up call event handlers
    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      isMicrophoneMuted.value = isMuted;
    });

    // Show call notes modal
    if (apiCallData) {
      callDialog.value = {
        id: Number(apiCallData.sessionId),
        type: CommunicationType.call,
        data: {
          id: Number(apiCallData.sessionId),
          session_id: apiCallData.sessionId,
          status: CallSessionStatus.InProgress,
          status_label: t("COMMON.IN_PROGRESS"),
          call_disposition: "",
          call_sessionable: {
            business: {
              business_legal_name: apiCallData.callerBusinessName,
            },
            client_name: apiCallData.callerName,
          },
          is_outgoing: false,
        },
      };
    }

    return;
  }

  // Unexpected incoming call without pendingAutoAccept - reject it
  // In V2 flow, all incoming calls should be expected (user clicked Accept first)
  trackEvent(AnalyticsEvent.TwilioUnexpectedIncomingCallRejected);
  call.reject();
};

const handleTokenExpiring = async () => {
  const token = await fetchAccessToken();
  if (token && device.value) {
    toRaw(device.value)?.updateToken(token);
    trackEvent(AnalyticsEvent.TwilioTokenRefreshed);
  }
};

const handleError = (error: { code: number; message: string }) => {
  showMessage(t("ERRORS.TWILIO.DEVICE_ERROR"), "error");
  Sentry.captureException(error);
};

const setupDevice = async () => {
  if (device.value) {
    return;
  }

  const token = twilioAccessToken.value || (await fetchAccessToken());
  if (!token) {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: "setup_device_no_token",
    });
    return;
  }
  let newDevice: Device | null = null;
  try {
    newDevice = new Device(token, deviceOptions);
    await newDevice.register();
    newDevice.audio?.incoming(false);
    newDevice.on(TwilioCallEvents.Incoming, handleIncomingCall);
    newDevice.on(TwilioCallEvents.TokenWillExpire, handleTokenExpiring);
    newDevice.on(TwilioCallEvents.Error, handleError);
    callStore.setDevice(newDevice);
    trackEvent(AnalyticsEvent.TwilioDeviceRegistered);
  } catch (error) {
    // Destroy the partially-initialised device so its Twilio WebSocket is closed
    try {
      newDevice?.destroy();
    } catch {
      // ignore secondary error
    }
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.DeviceError,
      explanation: t("ERRORS.TWILIO.DEVICE_SET_UP_ERROR"),
    });
    showMessage(t("ERRORS.TWILIO.DEVICE_SET_UP_ERROR"), "error");
    Sentry.captureException(error);
  }
};

/**
 * Handle accepting an API-driven incoming call.
 *
 * API-initiated flow:
 * 1. Ensure device is registered (receives incoming calls)
 * 2. Set auto-accept flag
 * 3. Call API (which triggers REST API call to our device)
 * 4. Device incoming handler auto-accepts when call arrives
 */
const handleAcceptApiCall = async () => {
  if (isAcceptingCall.value) {
    return;
  }
  isAcceptingCall.value = true;

  callStore.stopIncomingSound();
  // Immediately notify ALL other tabs to stop ringing — before any async work
  callStore.notifyIncomingCallHandled();

  try {
    // Ensure device is registered and ready to receive incoming call
    if (!device.value) {
      await setupDevice();
    }

    if (!device.value) {
      throw new Error("Failed to create Twilio device");
    }

    // Set flag so device auto-accepts the incoming call
    setPendingAutoAccept(true);

    // Call API - it will trigger REST API call to our device
    const answerResponse = await dialerApi.answerCall("browser");

    if (!answerResponse.success) {
      setPendingAutoAccept(false);
      throw new Error(answerResponse.error || "Failed to answer call");
    }

    // Success - the device incoming handler (handleIncomingCall) will
    // auto-accept when the call arrives and set the connected state.
  } catch (error) {
    setPendingAutoAccept(false);
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.ConnectionError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_ACCEPT_CALL"),
    });
    showMessage(t("ERRORS.TWILIO.FAILED_TO_ACCEPT_CALL"), "error");
    callStore.clearCallState();
    dialerApi.resetCall();
    cleanupDevice();
  } finally {
    isAcceptingCall.value = false;
  }
};

const initiateOutgoingCall = async (session?: ActiveCallSession | null) => {
  if (!session?.data) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    return;
  }

  // Guard against re-entry: the activeCallSession watcher can fire during
  // the await setupDevice() below (Vue processes the reactive change we
  // make to activeCallSession on the next line). isDialing stays true
  // until connect succeeds or clearCallState runs on error.
  if (isDialing.value) {
    return;
  }

  isDialing.value = true;
  callDirection.value = TwilioCallDirection.Outgoing;
  activeCallSession.value = session;

  try {
    // Ephemeral device pattern: Create device only when needed for a call
    if (!device.value) {
      await setupDevice();
    }

    if (!device.value) {
      throw new Error("Failed to create Twilio device");
    }

    const call = await toRaw(device.value).connect({
      params: {
        To: session.to,
        sessionId: session.data.session_id ?? "",
      },
    });
    callStore.setActiveCall(call);
    isDialing.value = false;
    showCallTimer.value = true;
    trackEvent(AnalyticsEvent.TwilioOutgoingCall);

    // Start heartbeat for long calls
    dialerApi.startHeartbeat();

    // Subscribe to call channel to receive phone number updates
    if (session.data.id) {
      subscribedCallId.value = session.data.id.toString();
      subscribeToChannelEvents(CALL_CHANNEL, subscribedCallId.value);
    }

    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      isMicrophoneMuted.value = isMuted;
    });

    // Handle call errors (e.g., TwiML fetch failures from backend)
    call.on("error", (error: { code: number; message: string }) => {
      trackEvent(AnalyticsEvent.TwilioError, {
        description: TwilioCallErrors.ConnectionError,
        explanation:
          error.message || t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"),
      });
      showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
      callStore.clearCallState();
      dialerApi.resetCall();
      cleanupDevice();
    });

    // Handle rejected calls (callee rejected or line busy)
    call.on("reject", () => {
      callStore.clearCallState();
      cleanupDevice();
    });
  } catch (error) {
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.ConnectionError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"),
    });
    showMessage(t("ERRORS.TWILIO.FAILED_TO_INITIATE_CALL"), "error");
    callStore.clearCallState();
    dialerApi.resetCall();
    cleanupDevice();
  }
};

const handlePiPOpen = async () => {
  if (!isSupported.value) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_SUPPORTED"), "error");
    return;
  }

  const existingPip = window.documentPictureInPicture?.window;
  if (existingPip && !existingPip.closed) {
    existingPip.focus();
    return;
  }

  if (
    callStore.isCallActive &&
    !pipContainer.value &&
    !activeCallSession.value &&
    !callStore.apiIncomingCallData
  ) {
    showMessage(t("ERRORS.TWILIO.CALL_ACTIVE_OTHER_TAB"), "warning");
    return;
  }

  const pip = await openPiP({
    width: deviceSize.width,
    height: deviceSize.height,
  });
  if (!pip) {
    showMessage(t("ERRORS.TWILIO.PIP_NOT_OPEN"), "error");
    return;
  }
  // Create a container div in the PiP window
  const container = pip.document.createElement("div");
  container.id = "twilio-pip-root";
  container.style.height = "100%";
  pip.document.body.appendChild(container);
  pip.document.body.style.margin = "0";
  pip.document.body.style.padding = "0";
  pip.document.body.style.height = "100%";
  pip.document.documentElement.style.height = "100%";

  pipContainer.value = container;
  callStore.togglePipState(true);
  trackEvent(AnalyticsEvent.TwilioPipOpened);

  // Listen for PiP window close to properly clean up state
  pip.addEventListener("pagehide", handlePiPClose);

  // If there's a pending outgoing call and no device exists yet, initiate it.
  // When PiP is closed and reopened during an active call, the Twilio device
  // and call survive — we just need the UI to re-render (which happens
  // automatically via reactive store state). Only initiate if the device
  // is null, meaning the call hasn't been placed via Twilio yet.
  if (
    activeCallSession.value &&
    !device.value &&
    dialerApi.callState &&
    !terminalState.value &&
    !endingTimer.value
  ) {
    initiateOutgoingCall(activeCallSession.value);
  }
};

// ============================================
// Provides
// ============================================

provide("terminalState", terminalState);
provide("isEnding", isEnding);
provide("dismissTerminalState", dismissTerminalState);

// ============================================
// Lifecycle Hooks
// ============================================

onMounted(() => {
  callStore.setPiPSupported(isSupported.value);
  emitter.on(TWILIO_PIP_OPEN, handlePiPOpen);
  emitter.on(TWILIO_PIP_ACCEPT_API_CALL, handleAcceptApiCall);

  // NOTE: We do NOT setup device on mount.
  // Twilio token is fetched and device is created ephemerally
  // only when PiP opens for an actual call (outgoing or incoming).
});

onBeforeUnmount(dismissTerminalState);

onUnmounted(() => {
  if (pipWindow.value) {
    pipWindow.value.close();
  }
  emitter.off(TWILIO_PIP_OPEN, handlePiPOpen);
  emitter.off(TWILIO_PIP_ACCEPT_API_CALL, handleAcceptApiCall);
  setPendingAutoAccept(false);
  dialerApi.stopHeartbeat();
  cleanupDevice();
});

// ============================================
// Watchers
// ============================================

// Watch for outgoing call requests from this tab.
// PiP must be open (pipContainer exists). Device is created ephemerally
// inside initiateOutgoingCall if it doesn't exist yet.
watch(activeCallSession, (session) => {
  if (!session || !pipContainer.value) {
    return;
  }
  if (terminalState.value || endingTimer.value) {
    return;
  }
  // Don't re-initiate if a device already has an active call
  if (device.value && callStore.activeCall) {
    return;
  }
  initiateOutgoingCall(session);
});

// Watch for call requests from OTHER tabs via BroadcastChannel
watch(
  callRequestData,
  (payload) => {
    if (!payload) {
      return;
    }
    // Another tab is handling the incoming call - stop our sound
    if (payload.type === CALL_REQUEST_TYPES.INCOMING_CALL_HANDLED) {
      callStore.stopIncomingSound();
      return;
    }
    // Another tab silenced/muted the call - stop our sound
    if (payload.type === CALL_REQUEST_TYPES.CALL_SILENCED) {
      callStore.stopIncomingSound();
      isRingtoneSilenced.value = true;
      return;
    }
    // Another tab accepted the call and it connected - stop sound as safety net
    if (payload.type === CALL_REQUEST_TYPES.CALL_STARTED) {
      callStore.stopIncomingSound();
      return;
    }
    // Another tab ended/rejected/reset the call
    if (payload.type === CALL_REQUEST_TYPES.CALL_ENDED) {
      if (callStore.activeCall || callStore.incomingCall) {
        // This tab owns the SDK — disconnect and let handleCallEnded →
        // startCallEnding handle the graceful "Ending…" transition
        // so contact data stays visible instead of flashing "unknown".
        try {
          if (callStore.activeCall) {
            toRaw(callStore.activeCall).disconnect();
          }
          if (callStore.incomingCall) {
            toRaw(callStore.incomingCall).reject();
          }
        } catch {
          // ignore — call may already be ended
        }
      } else {
        callStore.clearCallState();
        cleanupDevice();
      }
      return;
    }
    // Phone number update from another tab - sync to our stores
    if (
      payload.type === CALL_REQUEST_TYPES.PHONE_NUMBER_UPDATE &&
      payload.phoneNumber
    ) {
      if (activeCallSession.value) {
        activeCallSession.value = {
          ...activeCallSession.value,
          data: {
            ...activeCallSession.value.data,
            from: payload.phoneNumber,
          } as typeof activeCallSession.value.data,
        };
      }
      return;
    }
    // Only handle outgoing call broadcasts if PiP is open in THIS tab
    if (!pipContainer.value) {
      return;
    }
    if (payload.type === CALL_REQUEST_TYPES.INITIATE_CALL && payload.session) {
      initiateOutgoingCall(payload.session);
    }
  },
  { immediate: false }
);

watch(callDialog, (newValue) => {
  if (newValue) {
    showCallDialog.value = true;
  }
});

// Watch for backend-centric call state changes
// Sync API call state to callStore for UI rendering (incoming calls, call notes modal)
watch(
  () => dialerApi.callState,
  (newState, oldState) => {
    // Handle incoming call state (ringing) from API — only if this browser is the active dialer.
    // When desktop dialer is online it has priority, so dashboard should not ring.
    if (newState?.status === "ringing" && oldState?.status !== "ringing") {
      if (dialerApi.isActiveDialer && dialerApi.isRegisteredAsDialer) {
        callStore.setIncomingCallFromApi({
          sessionId: newState.call_session_id?.toString() || "",
          callerName: newState.call_data?.caller_name || "",
          callerBusinessName: newState.call_data?.caller_business_name || "",
          callerIntegrationPartner:
            newState.call_data?.caller_integration_partner || "",
          calledNumber: newState.call_data?.called_number || "",
          from: newState.call_data?.from || "",
          applicationId: newState.call_data?.application_id ?? undefined,
        });
      }
      return;
    }

    // Handle ringing → in_call transition: stop ringtone as redundant safety net
    // This covers cases where BroadcastChannel messages were missed or coalesced
    if (newState?.status === "in_call" && oldState?.status === "ringing") {
      callStore.stopIncomingSound();
    }

    // Handle any terminal state from API
    if (newState?.is_terminal) {
      handleTerminalState(newState);
      return;
    }

    // Handle call ended (null state)
    if (!newState && oldState) {
      // If already showing a terminal screen, the subsequent state:null is
      // just cleanup — ignore it here, dismissTerminalState() handles that.
      if (terminalState.value) {
        return;
      }

      // If we're in the call ending window (waiting for a potential busy event),
      // ignore state:null — the ending timer will handle final cleanup.
      if (isEnding.value) {
        return;
      }

      const clearableStatuses: CallStateStatus[] = ["in_call", "ringing", "initiating"];
      if (clearableStatuses.includes(oldState.status)) {
        callStore.clearCallState();
        cleanupDevice();
      }
      return;
    }

    // Handle call notes modal for in_call state (existing logic)
    if (newState?.status !== "in_call") {
      return;
    }

    // Only trigger for inbound calls (incoming calls that were accepted)
    if (newState.call_data?.direction !== "inbound") {
      return;
    }

    // Don't trigger if already showing a dialog or if the dialog is already set
    if (callDialog.value || showCallDialog.value) {
      return;
    }

    // Don't trigger if we're handling the call locally via PiP
    // (local PiP will trigger modal via TwilioPiPDialer)
    if (device.value && pipContainer.value) {
      return;
    }

    // Set up call dialog for backend-centric inbound call
    const sessionId = newState.call_session_id;
    if (!sessionId) {
      return;
    }

    callDialog.value = {
      id: 0, // Will be updated when CallSession is fetched
      type: CommunicationType.call,
      data: {
        id: 0,
        session_id: sessionId.toString(),
        status: CallSessionStatus.InProgress,
        status_label: t("COMMON.IN_PROGRESS"),
        call_disposition: "",
        call_sessionable: {
          business: {
            business_legal_name: newState.call_data?.caller_business_name ?? "",
          },
          client_name: newState.call_data?.caller_name ?? "",
        },
        is_outgoing: false,
      },
    };
  }
);

// Update device token when it changes (e.g., manual refresh via RefreshVoiceToken)
watch(twilioAccessToken, (newToken) => {
  if (newToken && device.value) {
    toRaw(device.value)?.updateToken(newToken);
  }
});

// Watch for activation changes
watch(
  () => dialerApi.isActiveDialer,
  (isActive, wasActive) => {
    if (!isActive && wasActive) {
      // Browser is no longer the active dialer (e.g. desktop dialer came online)
      // Close PiP window and cleanup device
      if (pipWindow.value && !pipWindow.value.closed) {
        pipWindow.value.close();
      }
      if (device.value) {
        cleanupDevice();
      }
    }

    if (isActive && !wasActive) {
      // Browser became the active dialer (e.g. desktop dialer went offline).
      // Only handle if the browser is also registered (fully re-activated).
      if (!dialerApi.isRegisteredAsDialer) {
        return;
      }

      // If a call is already ringing, the callState watcher won't re-fire
      // because callState itself hasn't changed — handle it here.
      const state = dialerApi.callState;
      if (state?.status === "ringing") {
        callStore.setIncomingCallFromApi({
          sessionId: state.call_session_id?.toString() || "",
          callerName: state.call_data?.caller_name || "",
          callerBusinessName: state.call_data?.caller_business_name || "",
          callerIntegrationPartner:
            state.call_data?.caller_integration_partner || "",
          calledNumber: state.call_data?.called_number || "",
          from: state.call_data?.from || "",
          applicationId: state.call_data?.application_id ?? undefined,
        });
      }
    }
  }
);
</script>
